import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { HOME_HERO_SLIDES } from "../data/homePageServices";
import '../styles/home-hero-slider.css';
import { publicAssetUrl } from '../utils/publicAssetUrl';

/**
 * Even index (0, 2, 4…): `flex-row-reverse` → image left, content right.
 * Odd index: default row → content left, image right.
 * (DOM order is always content pane, then media — a11y-friendly.)
 */
export function isHeroSlideReversed(slideIndex) {
  return slideIndex % 2 === 0;
}

function HeroSliderArrow({
  className,
  style,
  onClick,
  direction,
}: {
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  direction: string;
}) {
  const next = direction === "next";
  return (
    <button
      type="button"
      className={`${className || ""} home-hero-slider__arrow home-hero-slider__arrow--${direction}`}
      style={style}
      onClick={onClick}
      aria-label={next ? "Next slide" : "Previous slide"}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        {next ? (
          <path
            fill="currentColor"
            d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
          />
        ) : (
          <path
            fill="currentColor"
            d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
          />
        )}
      </svg>
    </button>
  );
}

/** Outline variants from `HOME_HERO_SLIDES[].ctas[].variant` — see `home-hero-slider.css`. */
const CTA_OUTLINE_VARIANTS = new Set(["ghost", "lightOutline", "border", "borderRaised"]);
const CTA_PRIMARY_VARIANTS = new Set(["dark", "primary-sm", "primary"]);

function heroCtaRoleClass(variant) {
  if (CTA_OUTLINE_VARIANTS.has(variant)) {
    return "home-hero-slider__cta--outline";
  }
  if (CTA_PRIMARY_VARIANTS.has(variant) || !variant) {
    return "home-hero-slider__cta--primary";
  }
  return "home-hero-slider__cta--primary";
}

function HeroBackgroundVideo({ video, isActive = false }) {
  if (!isActive || (!video?.src && !video?.vimeoId)) return null;

  const vimeoSrc = video.vimeoId
    ? `https://player.vimeo.com/video/${video.vimeoId}?background=1&autoplay=1&muted=1&loop=1&autopause=0&controls=0&title=0&byline=0&portrait=0&dnt=1`
    : null;

  return (
    <div className="home-hero-slider__video-bg" aria-hidden="true">
      {vimeoSrc ? (
        <iframe
          title={video.label || "Decorative hero background video"}
          src={vimeoSrc}
          className="home-hero-slider__video-bg-media home-hero-slider__video-bg-iframe"
          allow="autoplay; fullscreen; picture-in-picture"
          tabIndex={-1}
        />
      ) : (
        <video
          className="home-hero-slider__video-bg-media"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src={publicAssetUrl(video.src)} type="video/mp4" />
        </video>
      )}
    </div>
  );
}

/** Styles: alignment (`slide-left` / `slide-right`) + role (`--primary` / `--outline`). See `home-hero-slider.css`. */
function HeroCta({ to, label, variant }) {
  const role = heroCtaRoleClass(variant);
  return (
    <Link
      to={to}
      className={`btn btn-medium btn-border btn-hover-shadow home-hero-slider__cta ${role}`}
    >
      <span className="text">{label}</span>
      <span className="semicircle" />
    </Link>
  );
}

function getTitleClassName(titleVariant) {
  const base = "slider-content-title home-hero-slider__heading";
  switch (titleVariant) {
    case "primary":
      return `${base} home-hero-slider__title--primary`;
    case "display":
      return `${base} c-dark home-hero-slider__title--display`;
    default:
      return base;
  }
}

function getLedeClassName(ledeVariant) {
  const base = "slider-content-text home-hero-slider__subtitle home-hero-slider__lede";
  if (ledeVariant === "inverse") {
    return `${base} c-white home-hero-slider__lede--inverse`;
  }
  return base;
}

/**
 * Video-backed hero: copy alternates left/right while the media artwork stays disabled.
 */
function HeroSlideInner({ slide, slideIndex, reduceMotion = false, isActive = false }) {
  const splitReversed = isHeroSlideReversed(slideIndex);
  const eyebrowCls = slide.eyebrowOnDark
    ? "home-hero-slider__eyebrow home-hero-slider__eyebrow--on-dark"
    : "home-hero-slider__eyebrow";
  const isPrimarySlide = slideIndex === 0 && slide.titleVariant === "primary";
  const TitleTag = isPrimarySlide ? "h1" : "h2";
  const titleClassName = `${getTitleClassName(slide.titleVariant)}${
    isPrimarySlide && !isActive ? " sr-only" : ""
  }`;
  const ledeClassName = getLedeClassName(slide.ledeVariant);
  const copyAriaHidden = !isActive && !isPrimarySlide;
  const hideSecondaryCopy = !isActive && isPrimarySlide;

  const ctas = (
    <div
      className="main-slider-btn-wrap home-hero-slider__btn-row"
      aria-hidden={hideSecondaryCopy ? true : undefined}
    >
      {slide.ctas.map((c) => (
        <HeroCta
          key={`${c.to}-${c.label}`}
          to={c.to}
          label={c.label}
          variant={c.variant}
        />
      ))}
    </div>
  );

  const copy = (
    <div
      className={`slider-content home-hero-slider__copy home-hero-slider__copy--split${splitReversed ? " home-hero-slider__copy--split-reversed" : ""}`}
      aria-hidden={copyAriaHidden ? true : undefined}
    >
      <p
        className={`${eyebrowCls} home-hero-slider__kicker`}
        aria-hidden={hideSecondaryCopy ? true : undefined}
      >
        {slide.eyebrow}
      </p>
      <TitleTag className={titleClassName}>{slide.title}</TitleTag>
      <p className={ledeClassName} aria-hidden={hideSecondaryCopy ? true : undefined}>
        {slide.subtitle}
      </p>
      {ctas}
    </div>
  );

  const paletteClass = slide.ctaPalette
    ? `home-hero-slider__cta-palette--${slide.ctaPalette}`
    : "";
  const videoClass = slide.backgroundVideo?.src
    || slide.backgroundVideo?.vimeoId
    ? "home-hero-slider__slide-inner--video"
    : "";

  return (
    <div
      className={`home-hero-slider__slide-inner ${splitReversed ? "slide-right" : "slide-left"} ${paletteClass} ${videoClass}`.trim()}
    >
      <HeroBackgroundVideo
        video={reduceMotion ? null : slide.backgroundVideo}
        isActive={isActive}
      />
      <div className="container home-hero-slider__container">
        <div
          className={`home-hero-slider__split${splitReversed ? " home-hero-slider__split--reversed" : ""}`}
        >
          <div className="home-hero-slider__pane home-hero-slider__pane--content">{copy}</div>
          {/* Media artwork is intentionally hidden while Vimeo banners provide the hero background.
          <div className="home-hero-slider__pane home-hero-slider__pane--media">
            <div className="slider-thumb home-hero-slider__thumb">
              <HeroSlideImage
                src={slide.image.src}
                priority={Boolean(slide.image.priority)}
                alt={slide.image.alt || slide.title}
              />
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );
}

const Slide = () => {
  const mainRef = useRef(null);
  const navRef = useRef(null);
  const [navMain, setNavMain] = useState(null);
  const [navThumb, setNavThumb] = useState(null);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    setNavMain(mainRef.current);
    setNavThumb(navRef.current);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mq || typeof mq.matches !== 'boolean') {
      return undefined;
    }
    const apply = () => {
      const reduced = mq.matches;
      setReduceMotion(reduced);
      if (reduced) {
        mainRef.current?.slickPause();
        setAutoplayPaused(true);
      }
    };
    apply();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    return undefined;
  }, []);

  const toggleAutoplay = useCallback(() => {
    const api = mainRef.current;
    setAutoplayPaused((prev) => {
      const next = !prev;
      if (api) {
        if (next) api.slickPause();
        else api.slickPlay();
      }
      return next;
    });
  }, []);

  const mainSettings = {
    asNavFor: navThumb,
    infinite: true,
    speed: 650,
    cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
    autoplay: !reduceMotion,
    autoplaySpeed: 5200,
    pauseOnHover: true,
    pauseOnFocus: true,
    arrows: true,
    dots: false,
    adaptiveHeight: false,
    lazyLoad: "ondemand",
    prevArrow: <HeroSliderArrow direction="prev" />,
    nextArrow: <HeroSliderArrow direction="next" />,
    afterChange: (index) => setActiveSlide(index),
  };

  const navSettings = {
    asNavFor: navMain,
    slidesToShow: 6,
    swipeToSlide: true,
    focusOnSelect: true,
    arrows: false,
    dots: false,
    infinite: true,
    responsive: [
      {
        breakpoint: 1200,
        settings: { slidesToShow: 4 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 2 },
      },
    ],
  };

  return (
    <section
      className="main-slider home-hero-slider"
      aria-roledescription="carousel"
      aria-label="Featured services"
    >
      <div className="home-hero-slider__toolbar">
        <button
          type="button"
          className="home-hero-slider__pause"
          onClick={toggleAutoplay}
          aria-pressed={autoplayPaused}
          aria-label={autoplayPaused ? "Resume slideshow" : "Pause slideshow"}
        >
          {autoplayPaused ? "Play" : "Pause"}
        </button>
      </div>

      <div className="home-hero-slider__main">
        <Slider ref={mainRef} {...mainSettings}>
          {HOME_HERO_SLIDES.map((slide, slideIndex) => {
            const alignClass = isHeroSlideReversed(slideIndex) ? "slide-right" : "slide-left";
            return (
              <div key={slide.id}>
                <div className={`${slide.slideClasses} ${alignClass}`}>
                  <HeroSlideInner
                    slide={slide}
                    slideIndex={slideIndex}
                    reduceMotion={reduceMotion}
                    isActive={slideIndex === activeSlide}
                  />
                </div>
              </div>
            );
          })}
        </Slider>
      </div>

      <div className="home-hero-slider__nav-wrap">
        <Slider ref={navRef} {...navSettings}>
          {HOME_HERO_SLIDES.map((slide, index) => (
            <div key={slide.id}>
              <button
                type="button"
                className={`slides-item ${slide.panelClass}`}
                aria-label={`Show slide ${index + 1}: ${slide.nav.title}`}
                aria-current={index === activeSlide ? "true" : undefined}
              >
                <div className="content">
                  <div className="text-wrap">
                    <span className="slides-title">{slide.nav.title}</span>
                    {slide.nav.subtitle ? (
                      <span className="slides-sub-title">{slide.nav.subtitle}</span>
                    ) : null}
                  </div>
                  <span className="slides-number" aria-hidden="true">
                    {slide.num}
                  </span>
                </div>
              </button>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default Slide;
